import { useState, useMemo, useEffect } from "react";
import { Plus } from "lucide-react";
import Button from "../ui/Button";
import ToolProfile from "./ToolProfile";
import ToolForm from "./ToolForm";
import ActualsMatrix from "./ActualsMatrix";
import OneOffsView from "./OneOffsView";

const SUB_TABS = [
  { id: "recurring", label: "Recurring" },
  { id: "one-offs", label: "One-offs" },
];

export default function Tools({ state, dashboard, dateFilter, initialToolId, onClearInitialTool }) {
  const [selectedId, setSelectedId] = useState(initialToolId || null);
  const [addOpen, setAddOpen] = useState(false);
  const [subTab, setSubTab] = useState("recurring");
  const [deptFilter, setDeptFilter] = useState("");
  const [costTypeFilter, setCostTypeFilter] = useState("");

  // Departments to populate the filter dropdown — union of configured list
  // and any dept tag actually used on a tool's allocations.
  const departments = useMemo(() => {
    const set = new Set(state.settings?.departments || []);
    for (const t of state.tools || []) {
      for (const a of t.offerAllocations || []) {
        if (a.scope === "department" && a.scopeId) set.add(a.scopeId);
      }
    }
    return [...set].sort();
  }, [state.settings?.departments, state.tools]);

  useEffect(() => {
    if (initialToolId && initialToolId !== selectedId) {
      setSelectedId(initialToolId);
      onClearInitialTool?.();
    }
  }, [initialToolId, selectedId, onClearInitialTool]);

  const selectedTool = useMemo(
    () => (state.tools || []).find((t) => t.id === selectedId) || null,
    [state.tools, selectedId]
  );

  if (selectedTool) {
    return (
      <ToolProfile
        tool={selectedTool}
        state={state}
        dashboard={dashboard}
        onBack={() => setSelectedId(null)}
      />
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-display font-semibold text-lg text-brand-ink dark:text-[var(--text-primary)]">Costs</h2>
          <p className="text-xs text-brand-slate mt-0.5">Every tool, subscription, credit pack and one-off purchase, auto-calculated from matched expenses. Year + month bounds come from the global filter.</p>
        </div>
        <Button onClick={() => setAddOpen(true)}>
          <Plus size={14} /> Add tool
        </Button>
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="inline-flex rounded-lg border border-gray-200 dark:border-[var(--border-soft)] bg-white dark:bg-[var(--bg-surface)] p-0.5">
          {SUB_TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setSubTab(t.id)}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                subTab === t.id
                  ? "bg-brand-ink text-white"
                  : "text-brand-slate hover:text-brand-ink dark:hover:text-[var(--text-primary)]"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <select
            value={deptFilter}
            onChange={(e) => setDeptFilter(e.target.value)}
            className={`px-3 py-1.5 border rounded-lg text-xs bg-white dark:bg-[var(--bg-surface)] dark:border-[var(--border-soft)] ${
              deptFilter ? "border-brand-blue text-brand-blue-dark font-medium" : "border-gray-200"
            }`}
            title="Filter tools by which department absorbs the cost"
          >
            <option value="">All departments</option>
            <option value="__agency__">Agency (overhead)</option>
            <option value="__unassigned__">Unassigned</option>
            {departments.map((d) => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
          <select
            value={costTypeFilter}
            onChange={(e) => setCostTypeFilter(e.target.value)}
            className={`px-3 py-1.5 border rounded-lg text-xs bg-white dark:bg-[var(--bg-surface)] dark:border-[var(--border-soft)] ${
              costTypeFilter ? "border-brand-blue text-brand-blue-dark font-medium" : "border-gray-200"
            }`}
            title="Filter by cost type"
          >
            <option value="">All types</option>
            <option value="cogs">COGS only</option>
            <option value="opex">OPEX only</option>
            <option value="marketing">Marketing only</option>
          </select>
          {(deptFilter || costTypeFilter) && (
            <button
              onClick={() => { setDeptFilter(""); setCostTypeFilter(""); }}
              className="text-[11px] text-brand-slate hover:text-brand-ink"
              title="Clear filters"
            >
              Reset
            </button>
          )}
        </div>
      </div>

      {subTab === "recurring"
        ? <ActualsMatrix state={state} dateFilter={dateFilter} deptFilter={deptFilter} costTypeFilter={costTypeFilter} onSelectTool={setSelectedId} onOpenOneOffs={() => setSubTab("one-offs")} />
        : <OneOffsView state={state} dashboard={dashboard} dateFilter={dateFilter} deptFilter={deptFilter} costTypeFilter={costTypeFilter} onSelectTool={setSelectedId} />}

      <ToolForm
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onSubmit={(data) => dashboard.addTool(data)}
        offers={state.offers || []}
        departments={state.settings?.departments || []}
      />
    </div>
  );
}